import { useToast } from '@/hooks/useToast';
import { useEstablishmentStore } from '@/store/EstablishmentStore/EstablishmentStore';
import { useUpdateSchool, useResendActivationLink } from '@/api/hooks';
import { SchoolStatus, SchoolOutput } from '@/api/gql/graphql';
import { createMenuItem, isEstablishmentActive } from '../utils/helpers';
import { EstablishmentMenuItem } from '../utils/types';

// Hook pour gérer les actions des établissements
export const useEstablishmentActions = (
  establishments: SchoolOutput[] | undefined,
) => {
  const {
    openEstablishmentDeletionModal,
    closeEstablishmentDeletionModal,
    openEstablishmentSendMailModal,
    closeEstablishmentSendMailModal,
    openEstablishmentDetailModal,
    openEstablishmentEditModal,
    setSelectedEstablishmentEmail,
    deactivationMode,
    setDeactivationMode,
    selectedEstablishmentId,
    setSelectedEstablishmentId,
  } = useEstablishmentStore();

  const { showToast } = useToast();

  // Hooks pour les mutations
  const { mutateAsync: updateSchool, isPending: isUpdatingSchool } =
    useUpdateSchool();
  const { mutateAsync: resendActivationLink, isPending: isSendMailPending } =
    useResendActivationLink();

  // === ACTIONS DE DÉSACTIVATION/RÉACTIVATION ===
  const handleToggleEstablishmentStatus = (id: string) => {
    console.log('handleToggleEstablishmentStatus called with id:', id);
    const establishment = establishments?.find((est) => est._id === id);
    if (!establishment) return;

    console.log('Setting selectedEstablishmentId to:', id);
    setSelectedEstablishmentId(id);

    // Déterminer si on désactive ou réactive selon l'état actuel
    const mode = isEstablishmentActive(establishment.status || '')
      ? 'deactivate'
      : 'reactivate';

    setDeactivationMode(mode);
    openEstablishmentDeletionModal();
  };

  const executeToggleStatus = async () => {
    try {
      // Trouver l'établissement sélectionné
      const establishment = establishments?.find(
        (est) => est._id === selectedEstablishmentId,
      );
      console.log({ selectedEstablishmentId });

      if (!establishment) {
        showToast('Établissement non trouvé', 'error');
        return;
      }

      // Déterminer le nouveau statut selon le mode
      const newStatus =
        deactivationMode === 'deactivate'
          ? SchoolStatus.Inactive
          : SchoolStatus.Active;

      // Préparer les données pour la mise à jour
      const updateData = {
        name: establishment.name,
        address: establishment.address || undefined,
        contact: establishment.contact || undefined,
        iaCredit: establishment.iaCredit || undefined,
        type: establishment.type || undefined,
        status: newStatus,
      };

      await updateSchool({
        id: selectedEstablishmentId,
        input: updateData,
      });

      const successMessage =
        deactivationMode === 'deactivate'
          ? 'Établissement désactivé avec succès'
          : 'Établissement réactivé avec succès';

      showToast(successMessage, 'success');
      closeEstablishmentDeletionModal();
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : `Erreur lors de la ${deactivationMode === 'deactivate' ? 'désactivation' : 'réactivation'}`;
      showToast(errorMessage, 'error');
    }
  };

  // === ACTIONS D'ENVOI DE MAIL ===
  const handleSendMail = (id: string) => {
    // Trouver l'établissement et stocker son email directement
    const establishment = establishments?.find((est) => est._id === id);

    if (establishment?.contact?.email) {
      setSelectedEstablishmentId(id);
      setSelectedEstablishmentEmail(establishment.contact.email);
      openEstablishmentSendMailModal();
    } else {
      showToast("Email de l'établissement non trouvé", 'error');
    }
  };

  const executeSendMail = async (
    selectedEstablishmentEmail: string,
    selectedEstablishmentId: string,
  ) => {
    try {
      if (!selectedEstablishmentEmail) {
        showToast("Email de l'établissement non trouvé", 'error');
        return;
      }

      await resendActivationLink({
        email: selectedEstablishmentEmail,
        school: selectedEstablishmentId,
      });
      showToast('Mail de confirmation renvoyé avec succès', 'success');
      closeEstablishmentSendMailModal();
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Erreur lors de l'envoi du mail";
      showToast(errorMessage, 'error');
    }
  };

  // === ACTIONS DE DÉTAILS ET MODIFICATION ===
  const handleViewDetails = (id: string) => {
    setSelectedEstablishmentId(id);
    openEstablishmentDetailModal();
  };

  const handleEdit = (id: string) => {
    // Trouver l'établissement correspondant dans les données
    const establishmentToEdit = establishments?.find((est) => est._id === id);

    if (establishmentToEdit) {
      // Ouvrir le modal d'édition avec les données de l'établissement
      openEstablishmentEditModal(establishmentToEdit);
    } else {
      showToast('Établissement non trouvé', 'error');
    }
  };

  // === CRÉATION DU MENU D'ACTIONS ===
  const createActionsMenu = (
    establishmentId: string,
  ): EstablishmentMenuItem[] => {
    const establishment = establishments?.find(
      (est) => est._id === establishmentId,
    );
    const isActive = isEstablishmentActive(establishment?.status || '');

    return [
      createMenuItem('Details', 'font-[400]', () =>
        handleViewDetails(establishmentId),
      ),
      createMenuItem('Renvoyer mail', 'font-[400]', () =>
        handleSendMail(establishmentId),
      ),
      createMenuItem(isActive ? 'Désactiver' : 'Réactiver', 'font-[400]', () =>
        handleToggleEstablishmentStatus(establishmentId),
      ),
      createMenuItem('Modifier', 'font-[400]', () =>
        handleEdit(establishmentId),
      ),
    ];
  };

  return {
    // Actions de désactivation/réactivation
    handleToggleEstablishmentStatus,
    executeToggleStatus,
    isUpdatingSchool,
    deactivationMode,

    // Actions d'envoi de mail
    handleSendMail,
    executeSendMail,
    isSendMailPending,

    // Actions de détails et modification
    handleViewDetails,
    handleEdit,

    // Menu d'actions
    createActionsMenu,
  };
};
